import React, { useMemo, useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../../components/HeroSection';
import { Search, Filter, Calendar, User, ArrowRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  featured?: boolean;
}
const categories = [{
  id: 'all',
  label: 'All',
  color: 'bg-brand-orange',
  text: 'text-brand-orange',
  border: 'border-brand-orange'
}, {
  id: 'essay',
  label: 'Essays',
  color: 'bg-brand-magenta',
  text: 'text-brand-magenta',
  border: 'border-brand-magenta'
}, {
  id: 'reporting',
  label: 'Reporting',
  color: 'bg-brand-cyan',
  text: 'text-brand-cyan',
  border: 'border-brand-cyan'
}, {
  id: 'poetry',
  label: 'Poetry',
  color: 'bg-brand-lime',
  text: 'text-brand-lime',
  border: 'border-brand-lime'
}, {
  id: 'interview',
  label: 'Interviews',
  color: 'bg-brand-navy',
  text: 'text-brand-navy',
  border: 'border-brand-navy'
}, {
  id: 'visual',
  label: 'Visual',
  color: 'bg-brand-orange',
  text: 'text-brand-orange',
  border: 'border-brand-orange'
}, {
  id: 'qa',
  label: 'Q&A',
  color: 'bg-[#1C3A2E]',
  text: 'text-[#1C3A2E]',
  border: 'border-[#1C3A2E]'
}];
const allPosts: BlogPost[] = [{
  slug: 'why-we-built-regrowth',
  title: 'Why We Built ReGrowth',
  excerpt: 'A conversation with the founders about fat liberation, learning, and building something that matters.',
  author: 'ReGrowth Founders',
  date: '2025-01-20',
  category: 'qa',
  readTime: '15 min read',
  featured: true
}, {
  slug: 'architecture-of-exclusion',
  title: 'The Architecture of Exclusion: How Public Spaces Are Designed to Police Fat Bodies',
  excerpt: "From subway turnstiles to restaurant booths, the built environment tells fat people we don't belong. This essay examines how hostile architecture operates as a form of social control.",
  author: 'Jordan Miller',
  date: '2024-01-15',
  category: 'essay',
  readTime: '12 min read',
  featured: true
}, {
  slug: 'hunger-as-resistance',
  title: 'Hunger as Resistance',
  excerpt: 'A collection of poems reclaiming appetite in a world that demands our starvation.',
  author: 'Sam Rivera',
  date: '2024-01-10',
  category: 'poetry',
  readTime: '8 min read'
}, {
  slug: 'beyond-representation',
  title: "Beyond Representation: Why Seeing Fat People on TV Isn't Enough",
  excerpt: 'Visibility without structural change is just better PR for the same oppressive systems.',
  author: 'Alex Chen',
  date: '2024-01-05',
  category: 'essay',
  readTime: '15 min read'
}, {
  slug: 'bariatric-surgery-industry',
  title: 'Inside the Bariatric Surgery Industry: An Investigation',
  excerpt: 'How the weight loss surgery industry profits from desperation while downplaying risks and long-term complications.',
  author: 'Taylor Kim',
  date: '2023-12-20',
  category: 'reporting',
  readTime: '20 min read'
}, {
  slug: 'fat-joy-photo-series',
  title: 'Fat Futures: A Visual Essay',
  excerpt: 'A photographic exploration of fat joy, community, and radical imagination.',
  author: 'Riley Martinez',
  date: '2023-12-15',
  category: 'visual',
  readTime: '5 min read'
}, {
  slug: 'disability-justice-conversation',
  title: 'Fat & Disabled: A Conversation',
  excerpt: 'Three fat disabled activists discuss navigating medical systems, accessibility, and the intersection of fatphobia and ableism.',
  author: 'Multiple Authors',
  date: '2023-12-10',
  category: 'interview',
  readTime: '18 min read'
}, {
  slug: 'schools-weaponize-bmi',
  title: 'How Schools Weaponize BMI: An Investigation',
  excerpt: "BMI report cards, mandatory weigh-ins, and the surveillance of children's bodies in schools.",
  author: 'Morgan Lee',
  date: '2023-12-01',
  category: 'reporting',
  readTime: '16 min read'
}, {
  slug: 'body-as-archive',
  title: 'Body as Archive',
  excerpt: 'A long-form poem exploring memory, inheritance, and what our bodies hold.',
  author: 'Casey Johnson',
  date: '2023-11-25',
  category: 'poetry',
  readTime: '6 min read'
}, {
  slug: 'fat-fashion-reclaimed',
  title: 'Fat Fashion Reclaimed: Beyond the Plus-Size Industrial Complex',
  excerpt: 'How fat creators are building their own fashion ecosystems outside the mainstream industry.',
  author: 'Priya Desai',
  date: '2023-11-15',
  category: 'essay',
  readTime: '14 min read'
}, {
  slug: 'diet-culture-in-schools',
  title: 'Diet Culture in the Classroom',
  excerpt: 'An investigation into how anti-fat messaging is embedded in UK school curricula and what educators can do about it.',
  author: 'Morgan Lee',
  date: '2023-11-01',
  category: 'reporting',
  readTime: '18 min read'
}, {
  slug: 'fat-grief',
  title: 'Fat Grief: Mourning the Body You Were Told to Hate',
  excerpt: 'What happens when you stop fighting your body and start grieving the years you lost?',
  author: 'Sam Rivera',
  date: '2023-10-20',
  category: 'essay',
  readTime: '10 min read'
}];
function getCategoryMeta(categoryId: string) {
  return categories.find(c => c.id === categoryId) || categories[0];
}
export function BlogCatalogue() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const filteredPosts = useMemo(() => {
    let filtered = allPosts;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(post => post.title.toLowerCase().includes(q) || post.excerpt.toLowerCase().includes(q) || post.author.toLowerCase().includes(q) || post.category.toLowerCase().includes(q));
    }
    return filtered;
  }, [searchQuery, selectedCategory]);
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
  };
  const hasActiveFilters = searchQuery.trim() !== '' || selectedCategory !== 'all';
  return <div className="min-h-screen bg-brand-cream" data-id="element-380">
      <HeroSection title="Blog Catalogue" subtitle="Search and browse all posts — essays, reporting, poetry, interviews, and more." tag="Browse" bgColor="navy" data-id="element-381" />

      {/* Search & Filter Bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10 mb-8" data-id="element-382">
        <div className="bg-white border-2 border-brand-black shadow-lg p-6 space-y-6" data-id="element-383">
          {/* Search Input */}
          <div className="relative" data-id="element-384">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-385" />
            <input type="text" placeholder="Search by title, author, or keyword..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 focus:border-brand-navy focus:outline-none text-lg" data-id="element-386" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-black" data-id="element-387">
                <X className="h-5 w-5" data-id="element-388" />
              </button>}
          </div>

          {/* Category Filters */}
          <div className="flex items-center gap-3 flex-wrap" data-id="element-389">
            <div className="flex items-center gap-2 text-sm font-bold uppercase text-gray-500" data-id="element-390">
              <Filter className="h-4 w-4" data-id="element-391" />
              <span data-id="element-392">Filter:</span>
            </div>
            {categories.map(cat => <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all border-2 ${selectedCategory === cat.id ? `${cat.color} text-white border-transparent` : `bg-white text-gray-600 border-gray-200 hover:${cat.border}`}`} data-id="element-393">
                {cat.label}
              </button>)}
          </div>

          {/* Results count & clear */}
          <div className="flex items-center justify-between text-sm" data-id="element-394">
            <span className="text-gray-500" data-id="element-395">
              {filteredPosts.length}{' '}
              {filteredPosts.length === 1 ? 'post' : 'posts'} found
            </span>
            {hasActiveFilters && <button onClick={clearFilters} className="text-brand-orange font-bold hover:underline" data-id="element-396">
                Clear all filters
              </button>}
          </div>
        </div>
      </section>

      {/* Results Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-397">
        <AnimatePresence mode="wait" data-id="element-398">
          {filteredPosts.length > 0 ? <motion.div key={`${selectedCategory}-${searchQuery}`} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-399">
              {filteredPosts.map((post, index) => {
            const catMeta = getCategoryMeta(post.category);
            return <motion.div key={post.slug} initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.04
            }} data-id="element-400">
                    <Link to={`/blog/${post.slug}`} className="group block h-full" data-id="element-401">
                      <article className={`bg-white border-l-4 ${catMeta.border} border-y-2 border-r-2 border-y-brand-black border-r-brand-black p-6 hover:shadow-lg transition-shadow h-full flex flex-col`} data-id="element-402">
                        <div className="flex items-center gap-3 mb-3" data-id="element-403">
                          <span className={`text-xs font-bold uppercase tracking-wider ${catMeta.text}`} data-id="element-404">
                            {catMeta.label}
                          </span>
                          <span className="text-gray-300" data-id="element-405">·</span>
                          <span className="text-xs text-gray-500" data-id="element-406">
                            {post.readTime}
                          </span>
                          {post.featured && <>
                              <span className="text-gray-300" data-id="element-407">·</span>
                              <span className="text-xs font-bold uppercase tracking-wider text-brand-orange" data-id="element-408">
                                Featured
                              </span>
                            </>}
                        </div>

                        <h3 className="text-xl font-black mb-3 text-brand-black group-hover:text-brand-orange transition-colors leading-tight" data-id="element-409">
                          {post.title}
                        </h3>

                        <p className="text-gray-700 text-sm mb-6 leading-relaxed flex-grow" data-id="element-410">
                          {post.excerpt}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto" data-id="element-411">
                          <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-412">
                            <div className="flex items-center gap-1" data-id="element-413">
                              <User className="h-3 w-3" data-id="element-414" />
                              <span data-id="element-415">{post.author}</span>
                            </div>
                            <span className="text-gray-300" data-id="element-416">·</span>
                            <div className="flex items-center gap-1" data-id="element-417">
                              <Calendar className="h-3 w-3" data-id="element-418" />
                              <span data-id="element-419">
                                {new Date(post.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                              </span>
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-brand-orange opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" data-id="element-420" />
                        </div>
                      </article>
                    </Link>
                  </motion.div>;
          })}
            </motion.div> : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-center py-20" data-id="element-421">
              <p className="text-2xl font-black text-gray-300 mb-4" data-id="element-422">
                No posts found
              </p>
              <p className="text-gray-500 mb-6" data-id="element-423">
                Try adjusting your search or filters.
              </p>
              <button onClick={clearFilters} className="px-6 py-3 bg-brand-orange text-white font-bold uppercase tracking-wide hover:bg-brand-magenta transition-colors" data-id="element-424">
                Clear Filters
              </button>
            </motion.div>}
        </AnimatePresence>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-brand-orange text-white mt-8" data-id="element-425">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-426">
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-427">
            Never Miss a Post
          </h2>
          <p className="text-xl mb-8 opacity-90" data-id="element-428">
            Get new essays, reporting, and updates delivered to your inbox
            monthly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-429">
            <input type="email" placeholder="Your email" className="flex-1 px-4 py-3 text-brand-black border-2 border-white focus:outline-none" data-id="element-430" />
            <button className="px-8 py-3 bg-white text-brand-orange font-bold uppercase tracking-wide hover:bg-gray-100 transition-colors" data-id="element-431">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>;
}